// 📁 src/data/fichesPratiques.js
// Fiches pratiques — FR + EN
// Règle éditoriale : langage client non-technique, étapes concises (≤4),
// aucune révélation de méthodologie interne.

export const fichesPratiquesFR = [

  // ── Premiers pas ────────────────────────────────────────────────────────
    {
    slug: "premiers-pas",
    icon: "Compass",
    title: "Premiers pas dans Artkiv",
    description:
      "Créez un premier inventaire utile en quelques minutes, puis complétez-le à votre rythme.",
    steps: [
      "Vérifiez votre email de connexion et complétez les informations essentielles de votre profil.",
      "Ajoutez une première œuvre avec une photo, l'artiste, le titre, la date et les dimensions disponibles.",
      "Indiquez où l'œuvre se trouve et joignez les documents déjà en votre possession.",
      "Générez un premier inventaire, puis enrichissez les fiches progressivement — rien ne vous oblige à tout saisir d'un coup.",
    ],
    tips: [
      "Commencez par les œuvres que vous avez sous les yeux ou celles dont la documentation est la plus complète.",
    ],
    related_links: [
      { label: "Ajouter une œuvre", href: "/add-artwork" },
      { label: "Créer une collection", href: "/collections/new" },
      { label: "Rapports et exports", href: "/fiche/rapports" },
    ],
  },

  // ── Confidentialité ──────────────────────────────────────────────────────
    {
    slug: "confidentialite",
    icon: "ShieldCheck",
    title: "Confidentialité de votre collection",
    description:
      "Votre collection est privée par défaut. Vous décidez des accès accordés aux autres utilisateurs.",
    steps: [
      "Par défaut, aucun autre utilisateur ne peut consulter vos œuvres sans autorisation explicite.",
      "Lorsque vous partagez une collection, choisissez les œuvres concernées et le niveau d'accès : consultation ou édition selon les options disponibles.",
      "Révoquez un accès à tout moment depuis vos paramètres de partage.",
      "Les traitements techniques nécessaires au fonctionnement, à la sécurité et au support sont encadrés par la politique de confidentialité et le RGPD.",
    ],
    tips: [
      "N'accordez l'accès à la localisation, aux documents sensibles ou aux informations patrimoniales qu'aux personnes qui en ont réellement besoin.",
    ],
    related_links: [
      { label: "Paramètres de confidentialité", href: "/settings/privacy" },
      { label: "Charte de confidentialité", href: "/privacy-policy" },
    ],
  },

  // ── Gestion des œuvres ───────────────────────────────────────────────────
    {
    slug: "ajouter-oeuvre",
    icon: "Frame",
    title: "Ajouter une œuvre",
    description:
      "Créez une fiche avec les informations disponibles aujourd'hui, puis complétez-la au fil du temps.",
    steps: [
      "Depuis le menu principal, cliquez sur « Ajouter une œuvre » et importez au moins une photo nette.",
      "Renseignez les éléments essentiels : artiste, titre, date, technique et dimensions hors cadre lorsque vous les connaissez.",
      "Associez l'œuvre à une collection et indiquez son emplacement actuel.",
      "Ajoutez facture, certificat, provenance et notes d'état ; vous pouvez enregistrer une fiche encore incomplète.",
    ],
    related_links: [
      { label: "Bien remplir une fiche œuvre", href: "/fiche/bien-remplir-fiche" },
      { label: "Photos et documents", href: "/fiche/images-documents" },
      { label: "Gérer vos collections", href: "/fiche/collections" },
    ],
  },



  {
    slug: "bien-remplir-fiche",
    icon: "ListChecks",
    title: "Bien remplir une fiche œuvre",
    description:
      "Comprenez les principaux champs pour documenter correctement une œuvre sans vocabulaire technique inutile.",
    steps: [
      "Technique : indiquez comment l'œuvre est réalisée, par exemple « huile sur toile » ou « sérigraphie sur papier ».",
      "Catégorie : choisissez le type d'objet, par exemple peinture, sculpture, photographie, dessin ou estampe.",
      "Date et dimensions : utilisez une date approximative si nécessaire et mesurez l'œuvre hors cadre ; ajoutez les dimensions du cadre séparément.",
      "Acquisition et provenance : distinguez votre prix d'achat de l'historique des propriétaires antérieurs et joignez la source de chaque information.",
    ],
    tips: [
      "Vous pouvez enregistrer une fiche incomplète ; utilisez des formulations comme « vers 1990 » ou « non daté » plutôt que d'inventer une précision.",
      "Attribuez un numéro d'inventaire stable dès que possible.",
    ],
    related_links: [
      { label: "Ajouter une œuvre", href: "/add-artwork" },
      { label: "Comprendre les valeurs", href: "/fiche/comprendre-valeurs" },
    ],
  },
  {
    slug: "modifier-oeuvre",
    icon: "PenLine",
    title: "Modifier ou archiver une œuvre",
    description:
      "Mettez à jour une fiche à tout moment, ou archivez une pièce vendue ou donnée.",
    steps: [
      "Ouvrez la fiche de l'œuvre et cliquez sur « Modifier ».",
      "Corrigez ou complétez les informations, ajoutez des visuels ou des documents.",
      "Enregistrez — l'historique des modifications est conservé automatiquement.",
      "Pour une vente ou une donation, utilisez le bouton dédié : la fiche est archivée et la traçabilité préservée.",
    ],
    related_links: [
      { label: "Ventes et donations", href: "/fiche/ventes-donations" },
    ],
  },

    {
    slug: "images-documents",
    icon: "Paperclip",
    title: "Photos et documents",
    description:
      "Constituez un dossier visuel et documentaire exploitable pour l'inventaire, l'assurance et l'expertise.",
    steps: [
      "Ajoutez une vue générale nette, puis photographiez si possible le dos, la signature, le numéro d'édition, les étiquettes et les éventuels défauts.",
      "Pour une sculpture ou un objet, ajoutez plusieurs angles ; utilisez une lumière régulière, sans flash ni filtre.",
      "Déposez les documents disponibles : facture, certificat, preuve de paiement, expertise, constat d'état, contrat de prêt ou document de transport.",
      "Définissez la meilleure vue générale comme image principale et conservez les originaux papier en lieu sûr.",
    ],
    tips: [
      "Une photographie de détail doit rester rattachée à la même fiche que la vue générale.",
      "Évitez de recadrer une signature au point de perdre son emplacement sur l'œuvre.",
    ],
    related_links: [
      { label: "Ajouter une œuvre", href: "/add-artwork" },
      { label: "Bien remplir une fiche œuvre", href: "/fiche/bien-remplir-fiche" },
    ],
  },

  {
    slug: "editions",
    icon: "Hash",
    title: "Éditions et tirages multiples",
    description:
      "Gérez les œuvres en édition limitée : numérotation, tirages, statuts.",
    steps: [
      "Ouvrez la fiche de l'œuvre et activez l'option « Édition / Multiple ».",
      "Précisez le numéro de tirage, le total de l'édition et les éventuelles épreuves d'artiste.",
      "Chaque exemplaire peut avoir son propre emplacement, état et disponibilité.",
    ],
    related_links: [
      { label: "Support", href: "mailto:support@artkiv.com" },
    ],
  },

  // ── Import ───────────────────────────────────────────────────────────────
    {
    slug: "import-data",
    icon: "Download",
    access: "premium",
    title: "Importer votre inventaire existant",
    description:
      "Importez un tableur existant, puis contrôlez les données avant de compléter les fiches.",
    steps: [
      "Préparez une ligne par œuvre avec, au minimum, un titre ou numéro d'inventaire et le nom de l'artiste lorsqu'il est connu.",
      "Uniformisez les dates, devises et unités de dimensions avant l'import afin de limiter les corrections.",
      "Lancez l'import depuis l'espace prévu par votre formule, ou transmettez le fichier au support pour un accompagnement.",
      "Après import, vérifiez les doublons, les images, les dimensions et les valeurs avant de générer un rapport.",
    ],
    tips: [
      "Conservez une copie intacte du fichier d'origine avant toute modification.",
    ],
    related_links: [
      { label: "Contacter le support", href: "mailto:support@artkiv.com" },
    ],
  },

  // ── Collections ──────────────────────────────────────────────────────────
  {
    slug: "collections",
    icon: "FolderOpen",
    title: "Organiser vos collections",
    description:
      "Regroupez vos œuvres par thème, lieu, artiste ou tout autre critère qui vous ressemble.",
    steps: [
      "Créez une collection depuis le menu « Collections » et donnez-lui un nom.",
      "Ajoutez des œuvres existantes ou créez-en de nouvelles directement dans la collection.",
      "Partagez une collection entière avec un conseiller ou un expert d'un simple geste.",
    ],
    related_links: [
      { label: "Créer une collection", href: "/collections/new" },
    ],
  },

  {
    slug: "tags-filtres",
    icon: "Filter",
    title: "Retrouver une œuvre en un instant",
    description:
      "Tags, filtres et vues multiples pour naviguer dans votre collection sans effort.",
    steps: [
      "Ajoutez des mots-clés (tags) à chaque œuvre pour les regrouper librement.",
      "Depuis le tableau de bord, filtrez par artiste, médium, valeur, emplacement ou état.",
      "Passez de la vue grille à la vue liste selon votre préférence.",
    ],
    related_links: [],
  },

  // ── Analyse IA ───────────────────────────────────────────────────────────
    {
    slug: "analyse-ia",
    icon: "Sparkles",
    access: "premium",
    title: "Analyse IA de vos œuvres",
    description:
      "Utilisez l'IA comme aide à la documentation, à la conservation et à la lecture de vos œuvres.",
    steps: [
      "Activez votre consentement IA dans les paramètres avant le premier traitement.",
      "Choisissez une photographie nette et vérifiez les informations déjà saisies sur l'œuvre.",
      "Lancez le module disponible pour votre formule, puis relisez les suggestions avant de les conserver dans la fiche.",
      "Demandez une validation humaine lorsqu'une conclusion doit servir à une assurance, une succession, une vente ou une expertise.",
    ],
    tips: [
      "Les résultats sont indicatifs et ne remplacent ni l'examen matériel de l'œuvre ni l'avis d'un professionnel qualifié.",
      "Les images sont traitées uniquement selon votre consentement et les conditions décrites dans la politique de confidentialité.",
    ],
    related_links: [
      { label: "Paramètres IA", href: "/settings/privacy" },
      { label: "Faire estimer une œuvre", href: "/fiche/estimation" },
    ],
  },

  // ── Certificat blockchain ────────────────────────────────────────────────
    {
    slug: "blockchain",
    icon: "Fingerprint",
    access: "premium",
    status: "beta",
    title: "Empreinte numérique et traçabilité",
    description:
      "Créez une empreinte horodatée du dossier afin d'en renforcer la traçabilité et d'en vérifier l'intégrité dans le temps.",
    steps: [
      "Vérifiez les informations et documents du dossier avant de créer son empreinte numérique.",
      "L'empreinte cryptographique permet de constater qu'un ensemble de données existait dans un état déterminé à une date donnée.",
      "Conservez les certificats, factures et expertises d'origine : l'empreinte numérique ne prouve pas à elle seule l'authenticité matérielle de l'œuvre.",
    ],
    tips: [
      "Ce module ne remplace pas un certificat d'authenticité délivré par l'artiste, l'ayant droit, le comité ou l'expert compétent.",
      "La disponibilité du module dépend de votre formule et de son état de déploiement.",
    ],
    related_links: [
      { label: "Confidentialité", href: "/fiche/confidentialite" },
      { label: "Photos et documents", href: "/fiche/images-documents" },
    ],
  },

  // ── Estimation ───────────────────────────────────────────────────────────
  

  {
    slug: "comprendre-valeurs",
    icon: "BadgeEuro",
    title: "Comprendre les différentes valeurs",
    description:
      "Distinguez prix d'acquisition, estimation actuelle, fourchette expert, prix de vente et valeur d'assurance.",
    steps: [
      "Prix d'acquisition : montant payé lorsque l'œuvre est entrée dans votre collection, dans la devise de l'achat.",
      "Valeur estimée actuelle : indication de marché datée, susceptible d'évoluer et à accompagner de sa source.",
      "Fourchette basse et haute : intervalle retenu par un expert ou un outil de valorisation, distinct d'un prix de vente garanti.",
      "Valeur d'assurance : base discutée avec l'assureur selon le contrat ; elle peut différer du prix d'achat et de la valeur de revente.",
    ],
    tips: [
      "Ne remplacez jamais un ancien prix par une nouvelle estimation : conservez chaque donnée dans son champ et avec sa date.",
    ],
    related_links: [
      { label: "Faire estimer une œuvre", href: "/fiche/estimation" },
      { label: "Préparer un dossier d'assurance", href: "/fiche/estimation-assurance" },
    ],
  },
  {
    slug: "estimation",
    icon: "TrendingUp",
    title: "Faire estimer une œuvre",
    description:
      "Obtenez une valorisation indicative, datée et sourcée, adaptée à votre objectif : assurance, vente ou succession.",
    steps: [
      "Vérifiez l'identité de l'œuvre, ses dimensions, sa technique, son état, sa provenance et les documents disponibles.",
      "Lancez l'outil de valorisation disponible ou demandez l'intervention d'un expert selon votre formule et votre objectif.",
      "Enregistrez l'estimation indicative avec sa date, sa source et, lorsqu'elle existe, sa fourchette basse et haute.",
      "Pour une assurance, une succession, une donation ou une transaction importante, faites valider la valeur par un professionnel qualifié.",
    ],
    tips: [
      "Une estimation évolue dans le temps et doit être actualisée lorsque le marché, l'état de l'œuvre ou sa documentation changent.",
      "Une estimation est une fourchette à une date donnée, pas un prix garanti : le dernier prix d'adjudication n'est pas la valeur de votre œuvre.",
      "Réactualisez-la au moins une fois par an, et avant une assurance, une vente ou une succession.",
    ],
    related_links: [
      { label: "Comprendre les valeurs", href: "/fiche/comprendre-valeurs" },
      { label: "Préparer un dossier d'assurance", href: "/fiche/estimation-assurance" },
    ],
  },

    {
    slug: "estimation-assurance",
    icon: "Calculator",
    title: "Préparer un dossier d'assurance",
    description:
      "Rassemblez les informations que votre assureur ou votre courtier pourra examiner pour déterminer la couverture adaptée.",
    steps: [
      "Vérifiez la description, les dimensions, l'état, l'emplacement et les photographies de chaque œuvre concernée.",
      "Joignez les factures, certificats, expertises et estimations disponibles, en indiquant leur date et leur auteur.",
      "Générez l'inventaire ou le rapport disponible dans Artkiv, puis transmettez-le à votre assureur par le canal convenu avec lui.",
      "Demandez si une valeur agréée, une expertise physique ou des mesures de sécurité particulières sont nécessaires.",
    ],
    tips: [
      "La valeur d'assurance n'est pas toujours identique au prix d'achat ni à la valeur de revente.",
      "Faites réviser régulièrement la valeur d'assurance : si le marché ou l'état de l'œuvre évolue, une valeur trop ancienne peut ne plus refléter la réalité.",
    ],
    related_links: [
      { label: "Comprendre les valeurs", href: "/fiche/comprendre-valeurs" },
      { label: "Assurer votre collection", href: "/fiche/assurance" },
    ],
  },

  // ── Assurance ────────────────────────────────────────────────────────────
    {
    slug: "assurance",
    icon: "Shield",
    title: "Assurer votre collection",
    description:
      "Préparez un inventaire documenté afin d'échanger efficacement avec votre assureur ou votre courtier.",
    steps: [
      "Recensez les œuvres à couvrir et vérifiez leur emplacement, leur état et leurs valeurs les plus récentes.",
      "Générez un inventaire illustré avec les justificatifs disponibles.",
      "Demandez à votre assureur de confirmer les garanties, exclusions, franchises, mesures de sécurité et conditions de transport.",
      "Actualisez le dossier après une acquisition, une vente, un prêt, un déménagement ou une nouvelle estimation.",
    ],
    tips: [
      "Artkiv organise les informations ; seul l'assureur confirme la couverture contractuelle applicable.",
    ],
    related_links: [
      { label: "Préparer un dossier d'assurance", href: "/fiche/estimation-assurance" },
      { label: "Déclarer un sinistre", href: "/fiche/sinistre" },
    ],
  },

    {
    slug: "sinistre",
    icon: "AlertTriangle",
    title: "Réagir en cas de sinistre",
    description:
      "En cas de dommage, de vol ou de perte, retrouvez rapidement les éléments utiles à votre déclaration.",
    steps: [
      "Sécurisez les personnes et les lieux, puis contactez sans délai votre assureur ou votre courtier.",
      "Évitez toute intervention irréversible sur l'œuvre avant les instructions de l'assureur ou d'un restaurateur qualifié.",
      "Exportez la fiche, les photographies antérieures, les justificatifs de propriété, les estimations et les documents d'état disponibles.",
      "En cas de vol ou lorsque cela est requis, déposez plainte et conservez les références de la déclaration.",
    ],
    tips: [
      "Le dossier Artkiv facilite la constitution des pièces, mais l'assureur reste seul compétent pour apprécier la garantie et l'indemnisation.",
    ],
    related_links: [
      { label: "Assurer votre collection", href: "/fiche/assurance" },
    ],
  },

  // ── Transport & Emplacements ─────────────────────────────────────────────
  {
    slug: "emplacements",
    icon: "MapPin",
    title: "Localiser vos œuvres",
    description:
      "Sachez à tout moment où se trouve chaque pièce de votre collection.",
    steps: [
      "Créez vos emplacements (domicile, dépôt, galerie, prêt…) depuis le menu dédié.",
      "Assignez chaque œuvre à un emplacement depuis sa fiche.",
      "Consultez la vue globale pour voir la répartition de toute votre collection.",
    ],
    related_links: [
      { label: "Ajouter un emplacement", href: "/add-location" },
    ],
  },

    {
    slug: "transport-oeuvres",
    icon: "Truck",
    service: "accompanied",
    title: "Transporter vos œuvres",
    description:
      "Préparez le transport avec un professionnel qualifié et un dossier à jour.",
    steps: [
      "Vérifiez l'état de l'œuvre et réalisez des photographies datées avant l'enlèvement.",
      "Demandez au transporteur ses conditions d'emballage, de manutention, de stockage et de livraison.",
      "Confirmez avec votre assureur la couverture pendant le transport, notamment la portée d'une éventuelle garantie « clou à clou ».",
      "Téléchargez ou partagez uniquement les informations nécessaires avec le professionnel choisi.",
    ],
    tips: [
      "Une mise en relation peut être proposée selon les services disponibles ; le contrat reste conclu directement avec le prestataire concerné.",
    ],
    related_links: [
      { label: "Assurer votre collection", href: "/fiche/assurance" },
      { label: "Photos et documents", href: "/fiche/images-documents" },
    ],
  },

  // ── Conservation ─────────────────────────────────────────────────────────
  {
    slug: "conservation",
    icon: "Snowflake",
    title: "Prendre soin de vos œuvres",
    description:
      "Les bonnes conditions de conservation protègent la valeur de votre patrimoine.",
    steps: [
      "Vérifiez régulièrement température, humidité et luminosité dans les espaces de stockage.",
      "Évitez l'exposition directe à la lumière naturelle ou aux sources de chaleur.",
      "En cas de doute, consultez un restaurateur agréé — Artkiv peut vous mettre en relation.",
      "Enregistrez chaque intervention dans Artkiv pour garder un historique complet.",
    ],
    related_links: [
      { label: "Planifier la maintenance", href: "/fiche/maintenance" },
    ],
  },

  {
    slug: "maintenance",
    icon: "Wrench",
    title: "Planifier la maintenance",
    description:
      "Anticipez les interventions de conservation pour préserver vos œuvres dans le temps.",
    steps: [
      "Consultez l'état de conservation renseigné dans chaque fiche.",
      "Planifiez les interventions à venir (nettoyage, vernissage, encadrement, restauration).",
      "Enregistrez les coûts associés pour suivre le budget de conservation de votre collection.",
    ],
    related_links: [
      { label: "Gérer les coûts", href: "/fiche/couts" },
    ],
  },

  {
    slug: "couts",
    icon: "Receipt",
    title: "Suivre les coûts de collection",
    description:
      "Stockage, assurance, restauration, transport : visualisez ce que représente votre collection.",
    steps: [
      "Enregistrez chaque dépense dans la fiche de l'œuvre ou au niveau de la collection.",
      "Consultez les rapports de coûts depuis votre tableau de bord.",
      "Anticipez les investissements futurs pour ne jamais être pris de court.",
    ],
    related_links: [
      { label: "Rapports & exports", href: "/fiche/rapports" },
    ],
  },

  // ── Prêts ────────────────────────────────────────────────────────────────
  {
    slug: "pret",
    icon: "Send",
    title: "Prêter une œuvre à une institution",
    description:
      "Expositions, événements culturels : organisez vos prêts sereinement.",
    steps: [
      "Vérifiez que l'institution dispose d'une couverture d'assurance adaptée (« clou à clou »).",
      "Transmettez le dossier complet depuis Artkiv (état de conservation, photos, provenance).",
      "Enregistrez le prêt dans Artkiv pour en garder la traçabilité.",
    ],
    related_links: [
      { label: "Transporter vos œuvres", href: "/fiche/transport-oeuvres" },
    ],
  },

  // ── Rapports ─────────────────────────────────────────────────────────────
    {
    slug: "rapports",
    icon: "FileText",
    title: "Rapports et exports",
    description:
      "Transformez les données saisies en inventaires et rapports adaptés à leur destinataire.",
    steps: [
      "Vérifiez les œuvres incluses, les photographies, les valeurs et les informations sensibles avant l'export.",
      "Depuis la collection ou le tableau de bord, choisissez le format et le type de rapport disponibles pour votre formule.",
      "Téléchargez le document, contrôlez-le, puis transmettez-le par le canal sécurisé de votre choix.",
      "Régénérez le rapport après toute modification importante de la collection.",
    ],
    tips: [
      "Un export n'est fiable que si les fiches sont à jour ; vérifiez en priorité les dimensions, les valeurs, les emplacements et les documents.",
    ],
    related_links: [
      { label: "Gérer vos collections", href: "/fiche/collections" },
      { label: "Confidentialité", href: "/fiche/confidentialite" },
    ],
  },

  // ── Transmission ─────────────────────────────────────────────────────────
    {
    slug: "succession",
    icon: "Users",
    access: "premium",
    title: "Préparer la transmission",
    description:
      "Structurez les informations nécessaires à une discussion avec vos proches, votre notaire et vos conseils.",
    steps: [
      "Documentez la propriété, la provenance, les valeurs, les œuvres exceptionnelles et les éventuels droits démembrés.",
      "Renseignez les bénéficiaires ou personnes concernées uniquement lorsque vous souhaitez préparer des scénarios privés.",
      "Utilisez les simulations disponibles comme supports de réflexion, sans les considérer comme un calcul fiscal définitif.",
      "Faites valider toute décision de donation, partage, usufruit, legs ou succession par un notaire et, si nécessaire, un conseil fiscal.",
    ],
    tips: [
      "Les conséquences juridiques et fiscales dépendent de votre situation personnelle, de votre pays de résidence et du droit applicable.",
      "Artkiv organise les données et les scénarios ; il ne fournit pas de conseil juridique ou fiscal.",
    ],
    related_links: [
      { label: "Vendre ou donner une œuvre", href: "/fiche/ventes-donations" },
      { label: "Dation en paiement", href: "/fiche/dation-paiement" },
      { label: "Contact Family Office", href: "mailto:familyoffice@artkiv.com" },
    ],
  },

    {
    slug: "ventes-donations",
    icon: "Handshake",
    service: "accompanied",
    title: "Vendre ou donner une œuvre",
    description:
      "Préparez un dossier complet avant de contacter un acheteur, une maison de vente, un notaire ou une institution.",
    steps: [
      "Vérifiez le propriétaire, la provenance, les restrictions éventuelles, l'état, les dimensions et les documents de l'œuvre.",
      "Rassemblez les photographies, factures, certificats, estimations et éléments utiles au professionnel chargé de l'opération.",
      "Enregistrez la date, le type d'opération, le nouveau statut et les pièces finales lorsque la vente ou la donation est effectivement réalisée.",
      "Conservez la fiche archivée afin de préserver l'historique de la collection.",
    ],
    tips: [
      "La fiscalité, les obligations déclaratives et les garanties dues varient selon l'opération et le pays ; demandez un avis professionnel.",
    ],
    related_links: [
      { label: "Proposer à une institution", href: "/fiche/proposer-institution" },
      { label: "Préparer une consignation", href: "/fiche/proposer-auction" },
    ],
  },

    {
    slug: "proposer-institution",
    icon: "Building2",
    service: "accompanied",
    title: "Proposer une œuvre à une institution",
    description:
      "Préparez un dossier clair avant de contacter un musée, une fondation ou un centre d'art.",
    steps: [
      "Identifiez une institution dont les collections, la programmation ou la politique d'acquisition correspondent à l'œuvre.",
      "Préparez une présentation concise avec visuels, technique, dimensions, provenance, historique d'exposition et conditions envisagées.",
      "Exportez le dossier depuis Artkiv, puis adressez-le au bon interlocuteur selon les procédures de l'institution.",
      "Enregistrez les échanges et la décision finale dans la fiche sans présenter la proposition comme acceptée avant confirmation écrite.",
    ],
    related_links: [
      { label: "Vendre ou donner une œuvre", href: "/fiche/ventes-donations" },
    ],
  },

    {
    slug: "proposer-auction",
    icon: "Gavel",
    service: "accompanied",
    title: "Préparer une consignation aux enchères",
    description:
      "Constituez le dossier demandé par une maison de vente avant toute décision de consignation.",
    steps: [
      "Sélectionnez des photographies nettes et rassemblez provenance, facture, certificat, dimensions et informations d'état.",
      "Préparez une estimation existante si vous en disposez, sans la présenter comme une garantie de prix d'adjudication.",
      "Exportez le dossier et transmettez-le à une ou plusieurs maisons de vente pour examen.",
      "Après acceptation, enregistrez les conditions de consignation, la date de vente, le numéro de lot et le résultat final.",
    ],
    tips: [
      "La maison de vente reste libre d'accepter ou de refuser l'œuvre et fixe ses propres conditions contractuelles.",
    ],
    related_links: [
      { label: "Vendre ou donner une œuvre", href: "/fiche/ventes-donations" },
    ],
  },

    {
    slug: "dation-paiement",
    icon: "Landmark",
    service: "accompanied",
    title: "Comprendre la dation en paiement",
    description:
      "Découvrez le principe de cette procédure exceptionnelle avant de consulter les professionnels compétents.",
    steps: [
      "En France, la dation peut, sous des conditions strictes, permettre de régler certains droits par la remise à l'État d'un bien présentant une haute valeur artistique ou historique.",
      "Rassemblez les éléments d'identification, de propriété, de provenance, d'authenticité, d'état et de valorisation de l'œuvre.",
      "Consultez un notaire, un avocat fiscaliste ou un spécialiste de la procédure avant toute démarche.",
      "Utilisez Artkiv pour organiser le dossier documentaire, sans considérer la plateforme comme l'autorité qui accepte ou instruit la demande.",
    ],
    tips: [
      "La recevabilité, le calendrier et les effets fiscaux dépendent du droit applicable et de la décision des autorités compétentes.",
    ],
    related_links: [
      { label: "Préparer la transmission", href: "/fiche/succession" },
      { label: "Contact Family Office", href: "mailto:familyoffice@artkiv.com" },
    ],
  },
];


// ── English version ─────────────────────────────────────────────────────────
export const fichesPratiquesEN = [

  // ── Getting started ──────────────────────────────────────────────────────
    {
    slug: "premiers-pas",
    icon: "Compass",
    title: "Getting started with Artkiv",
    description:
      "Create a useful first inventory in minutes, then complete it at your own pace.",
    steps: [
      "Confirm your sign-in email and complete the essential profile information.",
      "Add a first artwork with a photo, artist, title, date, and any dimensions you know.",
      "Record its current location and attach the documents already in your possession.",
      "Generate a first inventory, then enrich the records progressively — you do not need to complete everything at once.",
    ],
    tips: [
      "Start with artworks you can see today or those with the most complete documentation.",
    ],
    related_links: [
      { label: "Add an artwork", href: "/add-artwork" },
      { label: "Create a collection", href: "/collections/new" },
      { label: "Reports and exports", href: "/fiche/rapports" },
    ],
  },

  // ── Privacy ──────────────────────────────────────────────────────────────
    {
    slug: "confidentialite",
    icon: "ShieldCheck",
    title: "Your collection stays private",
    description:
      "Your collection is private by default. You decide which access rights other users receive.",
    steps: [
      "By default, no other user can view your artworks without your explicit permission.",
      "When sharing a collection, choose the relevant artworks and the available access level: view or edit.",
      "Revoke access at any time from your sharing settings.",
      "Technical processing required for operation, security, and support is governed by the privacy policy and GDPR.",
    ],
    tips: [
      "Only share location, sensitive documents, or estate information with people who genuinely need it.",
    ],
    related_links: [
      { label: "Privacy settings", href: "/settings/privacy" },
      { label: "Privacy policy", href: "/privacy-policy-en" },
    ],
  },

  // ── Artwork management ───────────────────────────────────────────────────
    {
    slug: "ajouter-oeuvre",
    icon: "Frame",
    title: "Add an artwork",
    description:
      "Create a record with the information available today, then complete it over time.",
    steps: [
      'From the main menu, click "Add an artwork" and upload at least one clear photograph.',
      "Enter the essentials: artist, title, date, medium, and unframed dimensions when known.",
      "Link the artwork to a collection and record its current location.",
      "Add the invoice, certificate, provenance, and condition notes; you may save an incomplete record.",
    ],
    related_links: [
      { label: "Complete an artwork record", href: "/fiche/bien-remplir-fiche" },
      { label: "Photos and documents", href: "/fiche/images-documents" },
      { label: "Manage your collections", href: "/fiche/collections" },
    ],
  },



  {
    slug: "bien-remplir-fiche",
    icon: "ListChecks",
    title: "Complete an artwork record",
    description:
      "Understand the main fields and document an artwork correctly without unnecessary technical language.",
    steps: [
      'Medium: describe how the artwork was made, such as "oil on canvas" or "screenprint on paper".',
      "Category: select the type of object, such as painting, sculpture, photography, drawing, or print.",
      "Date and dimensions: use an approximate date where needed and measure the artwork unframed; record frame dimensions separately.",
      "Acquisition and provenance: distinguish your purchase price from earlier ownership history and record the source of each fact.",
    ],
    tips: [
      'You may save an incomplete record; use wording such as "circa 1990" or "undated" rather than inventing precision.',
      "Assign a stable inventory number as early as possible.",
    ],
    related_links: [
      { label: "Add an artwork", href: "/add-artwork" },
      { label: "Understand artwork values", href: "/fiche/comprendre-valeurs" },
    ],
  },
  {
    slug: "modifier-oeuvre",
    icon: "PenLine",
    title: "Edit or archive an artwork",
    description:
      "Update a record at any time, or archive a sold or donated piece.",
    steps: [
      'Open the artwork record and click "Edit".',
      "Correct or complete the information, add new visuals or documents.",
      "Save — the edit history is preserved automatically.",
      "For a sale or donation, use the dedicated button: the record is archived with its full history intact.",
    ],
    related_links: [
      { label: "Sales and donations", href: "/fiche/ventes-donations" },
    ],
  },

    {
    slug: "images-documents",
    icon: "Paperclip",
    title: "Photos and documents",
    description:
      "Build a visual and documentary record suitable for inventory, insurance, and professional review.",
    steps: [
      "Add a clear overall view, then photograph the back, signature, edition number, labels, and any visible damage where possible.",
      "For sculpture or objects, add several angles and use even light without flash or filters.",
      "Upload available documents: invoice, certificate, proof of payment, appraisal, condition report, loan agreement, or transport record.",
      "Set the strongest overall view as the main image and keep original paper documents in a safe place.",
    ],
    tips: [
      "Keep detail photographs attached to the same record as the overall view.",
      "Do not crop a signature so tightly that its position on the artwork is lost.",
    ],
    related_links: [
      { label: "Add an artwork", href: "/add-artwork" },
      { label: "Complete an artwork record", href: "/fiche/bien-remplir-fiche" },
    ],
  },

  {
    slug: "editions",
    icon: "Hash",
    title: "Editions and multiple works",
    description:
      "Manage limited-edition works: numbering, print runs, individual statuses.",
    steps: [
      'Open the artwork record and enable the "Edition / Multiple" option.',
      "Specify the print number, total edition size, and any artist's proofs.",
      "Each copy can have its own location, condition, and availability status.",
    ],
    related_links: [
      { label: "Support", href: "mailto:support@artkiv.com" },
    ],
  },

  // ── Import ───────────────────────────────────────────────────────────────
    {
    slug: "import-data",
    icon: "Download",
    access: "premium",
    title: "Import your existing inventory",
    description:
      "Import an existing spreadsheet, then review the data before completing each record.",
    steps: [
      "Prepare one row per artwork with at least a title or inventory number and the artist name when known.",
      "Standardise dates, currencies, and dimension units before import to reduce corrections.",
      "Launch the import from the area available in your plan, or send the file to support for guided assistance.",
      "After import, review duplicates, images, dimensions, and values before generating a report.",
    ],
    tips: [
      "Keep an untouched copy of the original file before making changes.",
    ],
    related_links: [
      { label: "Contact support", href: "mailto:support@artkiv.com" },
    ],
  },

  // ── Collections ──────────────────────────────────────────────────────────
  {
    slug: "collections",
    icon: "FolderOpen",
    title: "Organise your collections",
    description:
      "Group your artworks by theme, location, artist, or any criteria that suits your collection.",
    steps: [
      'Create a collection from the "Collections" menu and give it a name.',
      "Add existing artworks or create new ones directly within the collection.",
      "Share an entire collection with an advisor or expert in a single action.",
    ],
    related_links: [
      { label: "Create a collection", href: "/collections/new" },
    ],
  },

  {
    slug: "tags-filtres",
    icon: "Filter",
    title: "Find any artwork instantly",
    description:
      "Tags, filters, and multiple views to navigate your collection effortlessly.",
    steps: [
      "Add keywords (tags) to each artwork to group them freely.",
      "From the dashboard, filter by artist, medium, value, location, or condition.",
      "Switch between grid view and list view as you prefer.",
    ],
    related_links: [],
  },

  // ── AI Analysis ──────────────────────────────────────────────────────────
    {
    slug: "analyse-ia",
    icon: "Sparkles",
    access: "premium",
    title: "AI analysis of your artworks",
    description:
      "Use AI as an aid to documentation, conservation, and interpretation.",
    steps: [
      "Enable AI consent in your settings before the first processing operation.",
      "Choose a clear photograph and review the information already recorded for the artwork.",
      "Run the module available in your plan, then review all suggestions before saving them.",
      "Request human validation whenever a conclusion will be used for insurance, estate planning, a sale, or a formal appraisal.",
    ],
    tips: [
      "Results are indicative and do not replace physical inspection or advice from a qualified professional.",
      "Images are processed only under your consent and the conditions described in the privacy policy.",
    ],
    related_links: [
      { label: "AI settings", href: "/settings/privacy" },
      { label: "Appraise an artwork", href: "/fiche/estimation" },
    ],
  },

  // ── Blockchain ───────────────────────────────────────────────────────────
    {
    slug: "blockchain",
    icon: "Fingerprint",
    access: "premium",
    status: "beta",
    title: "Digital fingerprint and traceability",
    description:
      "Create a timestamped fingerprint of the record to strengthen traceability and verify its integrity over time.",
    steps: [
      "Review the record and its documents before creating the digital fingerprint.",
      "The cryptographic fingerprint can show that a set of data existed in a defined state at a given date.",
      "Keep original certificates, invoices, and appraisals: the fingerprint alone does not prove the artwork's material authenticity.",
    ],
    tips: [
      "This module does not replace a certificate issued by the artist, rights holder, committee, or competent expert.",
      "Availability depends on your plan and the module's deployment status.",
    ],
    related_links: [
      { label: "Privacy", href: "/fiche/confidentialite" },
      { label: "Photos and documents", href: "/fiche/images-documents" },
    ],
  },

  // ── Appraisal ────────────────────────────────────────────────────────────
  

  {
    slug: "comprendre-valeurs",
    icon: "BadgeEuro",
    title: "Understand artwork values",
    description:
      "Distinguish purchase price, current estimate, expert range, asking price, and insurance value.",
    steps: [
      "Purchase price: the amount paid when the artwork entered your collection, in the acquisition currency.",
      "Current estimated value: a dated market indication that may change and should retain its source.",
      "Low and high range: an interval used by an expert or valuation tool, not a guaranteed sale price.",
      "Insurance value: a basis discussed with the insurer under the policy; it may differ from purchase price and resale value.",
    ],
    tips: [
      "Never overwrite an earlier price with a new estimate: keep each figure in its own field with its date.",
    ],
    related_links: [
      { label: "Appraise an artwork", href: "/fiche/estimation" },
      { label: "Prepare an insurance dossier", href: "/fiche/estimation-assurance" },
    ],
  },
  {
    slug: "estimation",
    icon: "TrendingUp",
    title: "Appraise an artwork",
    description:
      "Obtain an indicative valuation, dated and sourced, suited to your goal: insurance, sale or estate.",
    steps: [
      "Review the artwork's identity, dimensions, medium, condition, provenance, and available documents.",
      "Run the valuation tool available to you or request an expert depending on your plan and objective.",
      "Record the indicative estimate with its date, source, and low and high range where available.",
      "For insurance, estate planning, donation, or a major transaction, obtain validation from a qualified professional.",
    ],
    tips: [
      "An estimate changes over time and should be updated when the market, condition, or documentation changes.",
      "An estimate is a range at a given date, not a guaranteed price: the last auction price is not the value of your artwork.",
      "Update it at least once a year, and before insurance, a sale or an estate.",
    ],
    related_links: [
      { label: "Understand artwork values", href: "/fiche/comprendre-valeurs" },
      { label: "Prepare an insurance dossier", href: "/fiche/estimation-assurance" },
    ],
  },

    {
    slug: "estimation-assurance",
    icon: "Calculator",
    title: "Prepare an insurance dossier",
    description:
      "Gather the information your insurer or broker can review when determining appropriate cover.",
    steps: [
      "Review the description, dimensions, condition, location, and photographs of each artwork concerned.",
      "Attach available invoices, certificates, appraisals, and estimates with their dates and authors.",
      "Generate the inventory or report available in Artkiv, then send it through the channel agreed with your insurer.",
      "Ask whether an agreed value, physical appraisal, or specific security measures are required.",
    ],
    tips: [
      "Insurance value is not always the same as purchase price or resale value.",
      "Have the insured value reviewed regularly: if the market or the artwork's condition changes, an old value may no longer reflect reality.",
    ],
    related_links: [
      { label: "Understand artwork values", href: "/fiche/comprendre-valeurs" },
      { label: "Insure your collection", href: "/fiche/assurance" },
    ],
  },

  // ── Insurance ────────────────────────────────────────────────────────────
    {
    slug: "assurance",
    icon: "Shield",
    title: "Insure your collection",
    description:
      "Prepare a documented inventory for an effective discussion with your insurer or broker.",
    steps: [
      "List the artworks to be covered and review their locations, condition, and latest values.",
      "Generate an illustrated inventory with the available supporting documents.",
      "Ask your insurer to confirm cover, exclusions, deductibles, security measures, and transport conditions.",
      "Update the dossier after an acquisition, sale, loan, move, or new appraisal.",
    ],
    tips: [
      "Artkiv organises the information; only the insurer confirms the applicable contractual cover.",
    ],
    related_links: [
      { label: "Prepare an insurance dossier", href: "/fiche/estimation-assurance" },
      { label: "Respond to a loss", href: "/fiche/sinistre" },
    ],
  },

    {
    slug: "sinistre",
    icon: "AlertTriangle",
    title: "Respond to a loss",
    description:
      "In the event of damage, theft, or loss, retrieve the information needed for your declaration quickly.",
    steps: [
      "Protect people and the premises, then contact your insurer or broker without delay.",
      "Avoid irreversible treatment of the artwork before receiving instructions from the insurer or a qualified conservator.",
      "Export the record, earlier photographs, proof of ownership, estimates, and available condition documents.",
      "In the event of theft or where required, file a police report and retain the declaration reference.",
    ],
    tips: [
      "The Artkiv dossier helps assemble evidence, but the insurer alone determines cover and compensation.",
    ],
    related_links: [
      { label: "Insure your collection", href: "/fiche/assurance" },
    ],
  },

  // ── Locations & Transport ────────────────────────────────────────────────
  {
    slug: "emplacements",
    icon: "MapPin",
    title: "Locate your artworks",
    description:
      "Know at any moment where every piece in your collection is.",
    steps: [
      "Create your locations (home, storage, gallery, loan…) from the dedicated menu.",
      "Assign each artwork to a location from its record.",
      "Consult the global view to see the distribution of your entire collection.",
    ],
    related_links: [
      { label: "Add a location", href: "/add-location" },
    ],
  },

    {
    slug: "transport-oeuvres",
    icon: "Truck",
    service: "accompanied",
    title: "Transport your artworks",
    description:
      "Prepare the move with a qualified professional and an up-to-date artwork dossier.",
    steps: [
      "Review the condition and take dated photographs before collection.",
      "Ask the transporter about packing, handling, storage, and delivery conditions.",
      'Confirm transit cover with your insurer, including the scope of any "nail-to-nail" protection.',
      "Download or share only the information required by the chosen professional.",
    ],
    tips: [
      "An introduction may be offered where services are available; the contract remains directly between you and the provider.",
    ],
    related_links: [
      { label: "Insure your collection", href: "/fiche/assurance" },
      { label: "Photos and documents", href: "/fiche/images-documents" },
    ],
  },

  // ── Conservation ─────────────────────────────────────────────────────────
  {
    slug: "conservation",
    icon: "Snowflake",
    title: "Care for your artworks",
    description:
      "Good conservation conditions protect the value of your heritage.",
    steps: [
      "Regularly check temperature, humidity, and light levels in storage areas.",
      "Avoid direct exposure to natural light or heat sources.",
      "If in doubt, consult an accredited conservator — Artkiv can make the introduction.",
      "Log every intervention in Artkiv to maintain a complete history.",
    ],
    related_links: [
      { label: "Plan maintenance", href: "/fiche/maintenance" },
    ],
  },

  {
    slug: "maintenance",
    icon: "Wrench",
    title: "Plan maintenance",
    description:
      "Anticipate conservation work to preserve your artworks over time.",
    steps: [
      "Review the condition recorded in each artwork record.",
      "Schedule upcoming interventions (cleaning, varnishing, framing, restoration).",
      "Log associated costs to monitor your collection's conservation budget.",
    ],
    related_links: [
      { label: "Track costs", href: "/fiche/couts" },
    ],
  },

  {
    slug: "couts",
    icon: "Receipt",
    title: "Track collection costs",
    description:
      "Storage, insurance, restoration, transport: see what your collection truly represents.",
    steps: [
      "Log each expense in the artwork record or at collection level.",
      "Review cost reports from your dashboard.",
      "Anticipate future investments so you're never caught off guard.",
    ],
    related_links: [
      { label: "Reports & exports", href: "/fiche/rapports" },
    ],
  },

  // ── Loans ────────────────────────────────────────────────────────────────
  {
    slug: "pret",
    icon: "Send",
    title: "Loan an artwork to an institution",
    description:
      "Exhibitions, cultural events: organise your loans with confidence.",
    steps: [
      'Verify that the institution has appropriate insurance coverage ("nail to nail").',
      "Share the complete dossier from Artkiv (condition report, photos, provenance).",
      "Record the loan in Artkiv to maintain full traceability.",
    ],
    related_links: [
      { label: "Transport your artworks", href: "/fiche/transport-oeuvres" },
    ],
  },

  // ── Reports ──────────────────────────────────────────────────────────────
    {
    slug: "rapports",
    icon: "FileText",
    title: "Reports and exports",
    description:
      "Turn recorded data into inventories and reports suited to their recipient.",
    steps: [
      "Review included artworks, photographs, values, and sensitive information before export.",
      "From the collection or dashboard, choose the report type and format available in your plan.",
      "Download and review the document, then send it through a secure channel of your choice.",
      "Generate a new report after any material change to the collection.",
    ],
    tips: [
      "An export is only as reliable as the records behind it; prioritise dimensions, values, locations, and documents.",
    ],
    related_links: [
      { label: "Manage your collections", href: "/fiche/collections" },
      { label: "Privacy", href: "/fiche/confidentialite" },
    ],
  },

  // ── Estate planning ──────────────────────────────────────────────────────
    {
    slug: "succession",
    icon: "Users",
    access: "premium",
    title: "Plan the transfer of your collection",
    description:
      "Structure the information needed for discussions with your family, notary, and advisers.",
    steps: [
      "Document ownership, provenance, values, exceptional works, and any divided ownership rights.",
      "Add beneficiaries or relevant people only when preparing private scenarios.",
      "Use available simulations as discussion aids, not as definitive tax calculations.",
      "Have every gift, division, life-interest, bequest, or estate decision validated by a notary and, where necessary, a tax adviser.",
    ],
    tips: [
      "Legal and tax consequences depend on your personal circumstances, residence, and applicable law.",
      "Artkiv organises data and scenarios; it does not provide legal or tax advice.",
    ],
    related_links: [
      { label: "Sell or donate an artwork", href: "/fiche/ventes-donations" },
      { label: "Dation en paiement", href: "/fiche/dation-paiement" },
      { label: "Family Office contact", href: "mailto:familyoffice@artkiv.com" },
    ],
  },

    {
    slug: "ventes-donations",
    icon: "Handshake",
    service: "accompanied",
    title: "Sell or donate an artwork",
    description:
      "Prepare a complete dossier before contacting a buyer, auction house, notary, or institution.",
    steps: [
      "Review ownership, provenance, restrictions, condition, dimensions, and documentation.",
      "Gather photographs, invoices, certificates, estimates, and information required by the professional handling the operation.",
      "Record the date, operation type, new status, and final documents once the sale or donation has actually completed.",
      "Keep the record archived to preserve the collection history.",
    ],
    tips: [
      "Tax, reporting duties, and warranties vary by operation and country; obtain professional advice.",
    ],
    related_links: [
      { label: "Propose to an institution", href: "/fiche/proposer-institution" },
      { label: "Prepare an auction consignment", href: "/fiche/proposer-auction" },
    ],
  },

    {
    slug: "proposer-institution",
    icon: "Building2",
    service: "accompanied",
    title: "Propose an artwork to an institution",
    description:
      "Prepare a clear dossier before contacting a museum, foundation, or arts centre.",
    steps: [
      "Identify an institution whose collection, programme, or acquisition policy fits the artwork.",
      "Prepare a concise presentation with images, medium, dimensions, provenance, exhibition history, and proposed terms.",
      "Export the dossier from Artkiv and send it to the appropriate contact according to the institution's procedures.",
      "Record exchanges and the final decision without presenting the proposal as accepted before written confirmation.",
    ],
    related_links: [
      { label: "Sell or donate an artwork", href: "/fiche/ventes-donations" },
    ],
  },

    {
    slug: "proposer-auction",
    icon: "Gavel",
    service: "accompanied",
    title: "Prepare an auction consignment",
    description:
      "Build the dossier an auction house will review before deciding whether to accept a consignment.",
    steps: [
      "Select clear photographs and gather provenance, invoice, certificate, dimensions, and condition information.",
      "Include an existing estimate where available, without presenting it as a guaranteed hammer price.",
      "Export the dossier and submit it to one or more auction houses for review.",
      "Once accepted, record the consignment terms, sale date, lot number, and final result.",
    ],
    tips: [
      "The auction house remains free to accept or decline the artwork and sets its own contractual terms.",
    ],
    related_links: [
      { label: "Sell or donate an artwork", href: "/fiche/ventes-donations" },
    ],
  },

    {
    slug: "dation-paiement",
    icon: "Landmark",
    service: "accompanied",
    title: "Understanding dation en paiement",
    description:
      "Understand this exceptional procedure before consulting the appropriate professionals.",
    steps: [
      "In France, dation may, under strict conditions, allow certain tax liabilities to be settled by transferring an asset of major artistic or historical value to the State.",
      "Gather identification, ownership, provenance, authenticity, condition, and valuation records for the artwork.",
      "Consult a notary, tax lawyer, or specialist before taking any formal step.",
      "Use Artkiv to organise the documentation, without treating the platform as the authority that accepts or assesses the application.",
    ],
    tips: [
      "Eligibility, timing, and tax consequences depend on applicable law and the decision of the competent authorities.",
    ],
    related_links: [
      { label: "Plan the transfer", href: "/fiche/succession" },
      { label: "Family Office contact", href: "mailto:familyoffice@artkiv.com" },
    ],
  },
];
